import React from "react";

/** A fraction rendered as numerator over denominator */
const Frac: React.FC<{ top: React.ReactNode; bottom: React.ReactNode }> = ({
  top,
  bottom,
}) => (
  <span className="inline-flex flex-col items-center mx-0.5 -my-1">
    <span className="border-b border-gray-500 px-0.5 leading-tight text-[10px]">
      {top}
    </span>
    <span className="px-0.5 leading-tight text-[10px]">{bottom}</span>
  </span>
);

/** C(n, k) rendered with subscript-style */
const Comb: React.FC<{ n: number; k: number }> = ({ n, k }) => (
  <span className="inline-flex items-center">
    <span className="text-gray-400 mr-px">C</span>
    <span className="text-[9px] text-gray-400">(</span>
    <span className="text-amber-400/80">{n}</span>
    <span className="text-[9px] text-gray-400">,</span>
    <span className="text-amber-400/80">{k}</span>
    <span className="text-[9px] text-gray-400">)</span>
  </span>
);

export interface FormulaData {
  outs: number;
  nonOuts: number;
  remaining: number;
  cardsToCome: number;
  percent: number;
}

export interface RunnerRunnerFormulaData {
  comboCount: number;
  totalCombos: number;
  remaining: number;
  percent: number;
}

/**
 * Renders the probability formula nicely.
 *
 * 1 card to come:  C(outs,1) / C(remaining,1) = outs/remaining = X%
 * 2 cards to come: 1 − C(nonOuts,2) / C(remaining,2) = X%
 */
const FormulaDisplay: React.FC<{ data: FormulaData }> = ({ data }) => {
  const { outs, nonOuts, remaining, cardsToCome, percent } = data;

  if (cardsToCome === 1) {
    return (
      <span className="inline-flex items-center flex-wrap gap-x-1 text-[11px] text-gray-400 font-mono">
        <Frac
          top={<Comb n={outs} k={1} />}
          bottom={<Comb n={remaining} k={1} />}
        />
        <span className="text-gray-600">=</span>
        <Frac top={outs} bottom={remaining} />
        <span className="text-gray-600">=</span>
        <span className="text-white font-semibold">{percent}%</span>
      </span>
    );
  }

  // 2 cards to come
  return (
    <span className="inline-flex items-center flex-wrap gap-x-1 text-[11px] text-gray-400 font-mono">
      <span>1 −</span>
      <Frac
        top={<Comb n={nonOuts} k={2} />}
        bottom={<Comb n={remaining} k={2} />}
      />
      <span className="text-gray-600">=</span>
      <span>1 −</span>
      <Frac
        top={
          <span>
            {nonOuts}×{nonOuts - 1}/2
          </span>
        }
        bottom={
          <span>
            {remaining}×{remaining - 1}/2
          </span>
        }
      />
      <span className="text-gray-600">=</span>
      <span className="text-white font-semibold">{percent}%</span>
    </span>
  );
};

/**
 * Renders formula for runner-runner: comboCount / C(remaining, 2) = X%
 */
export const RunnerRunnerFormula: React.FC<{
  data: RunnerRunnerFormulaData;
}> = ({ data }) => {
  const { comboCount, totalCombos, remaining, percent } = data;

  return (
    <span className="inline-flex items-center flex-wrap gap-x-1 text-[11px] text-gray-400 font-mono">
      <Frac
        top={<span>{comboCount}</span>}
        bottom={<Comb n={remaining} k={2} />}
      />
      <span className="text-gray-600">=</span>
      <Frac top={comboCount} bottom={totalCombos} />
      <span className="text-gray-600">=</span>
      <span className="text-white font-semibold">{percent}%</span>
    </span>
  );
};

export default FormulaDisplay;
